import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Container, Header, Segment, Button, Message, Table, Label, Icon, TextArea, Form, Menu,
} from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';

/**
 * Inviting congregants, and seeing where each invitation stopped.
 *
 * **The list is the feature, not the form.** Sending is easy; the hard part is knowing which of
 * forty invitations stalled and why, because the three failure modes need three different
 * responses:
 *
 *   - **Invited, never opened** — the email did not arrive. Re-send, or hand the link over another
 *     way.
 *   - **Opened, never finished** — they clicked and got stuck on the form. Worth a phone call.
 *   - **Joined, never returned** — they set a password once. They need the app, not another email.
 *
 * So the stage filter sits above the table rather than being a column you sort, and each filter
 * names the problem rather than the state.
 */

const INVITES = gql`
  query CongregantInvites {
    canInviteCongregants
    congregantInvites {
      invitationId email name role invitedAt openedAt acceptedAt firstLoginAt expiresAt stage invitedBy
    }
  }
`;

const INVITE = gql`
  mutation InviteCongregants($emails: [String!]!) {
    inviteCongregants(emails: $emails) { email outcome inviteLink emailStatus }
  }
`;

const RESEND = gql`
  mutation ResendCongregantInvite($invitationId: Int!) {
    resendCongregantInvite(invitationId: $invitationId) { email emailStatus inviteLink }
  }
`;

/** Each filter names a problem, not a state — that is what makes the screen usable. */
const FILTERS: { key: string; label: string; stages: string[] | null; hint: string }[] = [
  { key: 'all', label: 'Everyone', stages: null, hint: 'Every invitation ever sent.' },
  {
    key: 'INVITED', label: 'No reply', stages: ['INVITED'],
    hint: "Sent, never opened — the email may not have arrived. Re-send, or check for a bounce.",
  },
  {
    key: 'OPENED', label: 'Started, stuck', stages: ['OPENED'],
    hint: 'They clicked the link but never set a password. This is the group worth phoning.',
  },
  {
    key: 'ACCEPTED', label: 'Joined, not back', stages: ['ACCEPTED'],
    hint: 'They set a password and have not signed in since. They probably need the app.',
  },
  { key: 'ACTIVE', label: 'Using Joyya', stages: ['ACTIVE'], hint: 'Signed up and signed in. Nothing to do.' },
  {
    key: 'EXPIRED', label: 'Expired', stages: ['EXPIRED', 'REVOKED'],
    hint: 'The link ran out or was withdrawn. Re-send to give them a fresh one.',
  },
];

const STAGE_LABEL: Record<string, { text: string; colour: any }> = {
  INVITED: { text: 'Invited', colour: undefined },
  OPENED: { text: 'Opened the link', colour: 'yellow' },
  ACCEPTED: { text: 'Joined', colour: 'blue' },
  ACTIVE: { text: 'Using Joyya', colour: 'green' },
  EXPIRED: { text: 'Expired', colour: 'grey' },
  REVOKED: { text: 'Withdrawn', colour: 'grey' },
};

const when = (iso: string | null) => {
  if (!iso) return <span style={{ color: '#ccc' }}>—</span>;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });
};

export const Invites = () => {
  const { data, loading, refetch } = useQuery(INVITES, { fetchPolicy: 'network-only' });
  const [invite, { loading: sending }] = useMutation(INVITE);
  const [resend] = useMutation(RESEND);

  const [raw, setRaw] = useState('');
  const [filter, setFilter] = useState('all');
  const [results, setResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (loading) return null;

  if (!data?.canInviteCongregants) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const rows = (data?.congregantInvites ?? []) as any[];
  const active = FILTERS.find((f) => f.key === filter)!;
  const shown = active.stages == null ? rows : rows.filter((r) => active.stages!.includes(r.stage));

  // Addresses come in pasted from a spreadsheet, an email, or typed one per line — so anything
  // that separates them is a separator rather than demanding one format.
  const parsed = Array.from(new Set(raw.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s !== '')));
  const invalid = parsed.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

  const countFor = (f: typeof FILTERS[number]) =>
    f.stages == null ? rows.length : rows.filter((r) => f.stages!.includes(r.stage)).length;

  const send = async () => {
    setError(null);
    setResults(null);
    try {
      const res = await invite({ variables: { emails: parsed } });
      setResults(res.data.inviteCongregants);
      setRaw('');
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? 'Could not send those invitations.');
    }
  };

  return (
    <ResponsiveContainer>
      <Container style={{ paddingTop: '2em', maxWidth: '1000px' }}>
        <Header as="h1">
          Invite people
          <Header.Subheader>
            They join as members of your organisation — they see its updates, and nothing is
            administered by them.
          </Header.Subheader>
        </Header>

        <Segment>
          <Form>
            <Form.Field>
              <label>Email addresses</label>
              <TextArea
                rows={3}
                placeholder={'someone@example.com\nanother@example.com'}
                value={raw}
                onChange={(e: any) => setRaw(e.target.value)}
              />
              <div style={{ color: '#767676', marginTop: '0.4em' }}>
                One per line, or separated by commas — paste a list straight in.
              </div>
            </Form.Field>

            {invalid.length > 0 && (
              <Message warning>
                These don't look like email addresses: {invalid.join(', ')}
              </Message>
            )}
            {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}

            <Button
              primary
              loading={sending}
              disabled={sending || parsed.length === 0 || invalid.length > 0}
              onClick={send}
            >
              {parsed.length > 1 ? `Invite ${parsed.length} people` : 'Send invitation'}
            </Button>
          </Form>
        </Segment>

        {results && (
          <Message
            positive
            onDismiss={() => setResults(null)}
            header={`${results.length} invitation${results.length === 1 ? '' : 's'} processed`}
          >
            <ul style={{ marginTop: '0.5em' }}>
              {results.map((r, i) => (
                <li key={i}>
                  {r.email} — {r.outcome === 'INVITED' ? 'invited'
                    : r.outcome === 'ALREADY_MEMBER' ? 'already a member'
                      : 'already had an account, added'}
                  {/* "Accepted for delivery", never "sent" — the mail server taking a message is
                      not the same as delivering it, and a bounce can follow minutes later. */}
                  {r.emailStatus === 'ACCEPTED' && ' · email accepted for delivery'}
                  {r.emailStatus === 'REJECTED' && ' · email was rejected — check the address'}
                  {r.emailStatus === 'NOT_CONFIGURED' && ' · email is not configured here'}
                  {r.emailStatus === 'FAKED_IN_DEV' && ' · development, no email sent'}
                </li>
              ))}
            </ul>
          </Message>
        )}

        <Menu pointing secondary style={{ marginTop: '2em', flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <Menu.Item
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <Label circular size="tiny" style={{ marginLeft: '0.5em' }}>{countFor(f)}</Label>
            </Menu.Item>
          ))}
        </Menu>

        <p style={{ color: '#767676' }}>{active.hint}</p>

        {shown.length === 0
          ? <Message content="Nobody here." />
          : (
            <Table compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Person</Table.HeaderCell>
                  <Table.HeaderCell>Stage</Table.HeaderCell>
                  <Table.HeaderCell>Invited</Table.HeaderCell>
                  <Table.HeaderCell>Opened link</Table.HeaderCell>
                  <Table.HeaderCell>Joined</Table.HeaderCell>
                  <Table.HeaderCell>First sign-in</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              {/* Newest first — the oldest invitation sits at the bottom, so the people you just
                  invited are the ones you see without scrolling. */}
              <Table.Body>
                {shown.map((r) => (
                  <Table.Row key={r.invitationId}>
                    <Table.Cell>
                      {r.name || r.email}
                      {r.name && <div style={{ color: '#767676', fontSize: '0.9em' }}>{r.email}</div>}
                      {r.invitedBy && (
                        <div style={{ color: '#aaa', fontSize: '0.85em' }}>invited by {r.invitedBy}</div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="tiny" color={STAGE_LABEL[r.stage]?.colour}>
                        {STAGE_LABEL[r.stage]?.text ?? r.stage}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{when(r.invitedAt)}</Table.Cell>
                    <Table.Cell>{when(r.openedAt)}</Table.Cell>
                    <Table.Cell>{when(r.acceptedAt)}</Table.Cell>
                    <Table.Cell>{when(r.firstLoginAt)}</Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      {/* Only where it helps. Re-sending to somebody already using Joyya would
                          send a link that sets a password they already have. */}
                      {['INVITED', 'OPENED', 'EXPIRED'].includes(r.stage) && (
                        <Button
                          size="tiny"
                          onClick={async () => {
                            setError(null);
                            try {
                              await resend({ variables: { invitationId: r.invitationId } });
                              await refetch();
                            } catch (e: any) {
                              setError(e?.message ?? 'Could not re-send that invitation.');
                            }
                          }}
                        >
                          <Icon name="redo" /> Re-send
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
      </Container>
    </ResponsiveContainer>
  );
};
